import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subscription } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { federalState, federalStateOptions, getFederalStateName } from '../../../dave-data-module/entities/mdqx/customer-options.entity';
import { PracticeTypeEntity } from '../../../dave-data-module/entities/mdqx/practice-type.entity';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { State } from '../../../dave-data-module/State';
import { BaseActionTypes } from '../../../dave-data-module/State/actions/base.actions';
import { PatchCustomerOptionsRequest, PostCustomerOptionsRequest } from '../../../dave-data-module/State/actions/mdqx/customer-options.actions';
import { getCustomerOptions, getPracticeTypes, getPracticeTypesFetched } from '../../../dave-data-module/State/selectors/mdqx/selectors';
import { IDetailListTemplateData, IDetailListTemplateDataProperty } from '../../../dave-utils-module/dave-shared-components-module/components/detail-views/detail-list-template/detail-list-template.component';
import {
    SelectSearchOption
} from '../../../dave-utils-module/select-search/components/select-search/select-search.component';

@Component({
    selector: 'app-additional-customer-options',
    templateUrl: './additional-customer-options.component.html',
    styleUrls: ['./additional-customer-options.component.scss'],
})
export class AdditionalCustomerOptionsComponent implements OnInit, OnDestroy {
    @Input()
    get IsPX() {
        return this.isPx$.value;
    }
    set IsPX(value: boolean) {
        this.isPx$.next(value);
    }
    @Input() Inline = false;
    @Input() CustomerId: number = null;
    @Input()
    get Editing() {
        return this.Editing$.value;
    }
    set Editing(value: boolean) {
        this.Editing$.next(value);
    }
    @Output() private valid = new EventEmitter<boolean>();


    public Editing$ = new BehaviorSubject(false);
    private isPx$ = new BehaviorSubject(false);
    public Data$: Observable<IDetailListTemplateData>;
    // CustomerOptions FormGroup
    public PracticeTypes$ = getFetched$(this.store, getPracticeTypesFetched, getPracticeTypes);
    public CustomerOptionsForm = new FormGroup({
        BSNR: new FormControl<string | null>(null),
        FederalState: new FormControl<SelectSearchOption< {value: federalState }> | null>(null),
        IK: new FormControl<string | null>(null),
        PaymentTarget: new FormControl<string | null>(null),
        PracticeType: new FormControl<PracticeTypeEntity | null>(null),
    });
    private subscriptions: Subscription[] = [];
    constructor(private store: Store<State>) {}

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    ngOnInit(): void {
        this.Data$ = combineLatest([this.store.select(getCustomerOptions), this.Editing$, this.isPx$, this.PracticeTypes$]).pipe(
            map(([cos, editing, isPx, pt]) => {
                const co = cos.find((c) => c.CustomerId === this.CustomerId);
                const Properties: IDetailListTemplateDataProperty[] = [
                    {
                        value: co?.BSNR,
                        key: 'BSNR',
                        formControl: this.CustomerOptionsForm.controls.BSNR,
                    },
                    {
                        value: co?.FederalState ? getFederalStateName(co.FederalState) : null,
                        key: 'Bundesland',
                        formControl: this.CustomerOptionsForm.controls.FederalState,
                        options: {
                            specialInput: {
                                singleSelectSearch: {
                                    options: federalStateOptions,
                                    compareOptions: (a, b) => a?.value === b?.value,
                                },
                            },
                        },
                    },
                    {
                        value: co?.IK,
                        key: 'IK',
                        formControl: this.CustomerOptionsForm.controls.IK,
                    },
                    {
                        value: co?.PaymentTarget,
                        key: 'Zahlungsziel',
                        formControl: this.CustomerOptionsForm.controls.PaymentTarget,
                    },
                ];
                if (isPx) {
                    Properties.push({
                        value: co?.PracticeTypeId ? pt.find((p) => p.Id === co.PracticeTypeId)?.Name : null,
                        key: 'Praxisart',
                        formControl: this.CustomerOptionsForm.controls.PracticeType,
                        options: {
                            specialInput: {
                                selectSearch: pt.map((p) => ({
                                    optionValue: p.Id,
                                    optionLabel: p.Name,
                                })),
                            },
                        },
                    });
                }
                return co || editing ? ({ Properties } as IDetailListTemplateData) : null;
            }),
        );
        this.subscriptions.push(
            this.Editing$.pipe(
                filter((editing) => editing),
                withLatestFrom(this.store.select(getCustomerOptions).pipe(map((co) => co.find((c) => c.CustomerId === this.CustomerId))), this.PracticeTypes$),
            ).subscribe(([, co, pt]) => {
                this.CustomerOptionsForm.reset({
                    BSNR: co?.BSNR,
                    FederalState: co?.FederalState ? {value: co.FederalState, optionLabel: getFederalStateName(co.FederalState)} : null,
                    IK: co?.IK,
                    PaymentTarget: co?.PaymentTarget,
                    PracticeType: co?.PracticeTypeId ? pt.find((p) => p.Id === co.PracticeTypeId) : null,
                });
                this.valid.emit(this.CustomerOptionsForm.valid);
            }),
            this.CustomerOptionsForm.valueChanges.subscribe(() => this.valid.emit(this.CustomerOptionsForm.valid)),
        );
    }
    async submit() {
        const co = await firstValueFrom(this.store.select(getCustomerOptions)).then((co) => co.find((c) => c.CustomerId === this.CustomerId));
        if (this.CustomerOptionsForm.invalid) {
            this.store.dispatch(
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: 'Speichern fehlgeschlagen, bitte überprüfen Sie die Eingaben',
                    },
                }),
            );
        } else {
            if (co) {
                console.log(this.CustomerOptionsForm.value.FederalState)
                this.store.dispatch(
                    new PatchCustomerOptionsRequest({
                        id: co.Id,
                        BSNR: this.CustomerOptionsForm.value.BSNR,
                        federalState: this.CustomerOptionsForm.value.FederalState?.value,
                        IK: this.CustomerOptionsForm.value.IK,
                        paymentTarget: this.CustomerOptionsForm.value.PaymentTarget,
                        practiceTypeId: this.CustomerOptionsForm.value.PracticeType?.Id || null,
                    }),
                );
            } else if (
                this.CustomerOptionsForm.value.BSNR ||
                this.CustomerOptionsForm.value.FederalState ||
                this.CustomerOptionsForm.value.IK ||
                this.CustomerOptionsForm.value.PaymentTarget ||
                this.CustomerOptionsForm.value.PracticeType?.Id
            ) {
                this.store.dispatch(
                    new PostCustomerOptionsRequest({
                        BSNR: this.CustomerOptionsForm.value.BSNR,
                        federalState: this.CustomerOptionsForm.value.FederalState?.value,
                        IK: this.CustomerOptionsForm.value.IK,
                        paymentTarget: this.CustomerOptionsForm.value.PaymentTarget,
                        practiceTypeId: this.CustomerOptionsForm.value.PracticeType?.Id || null,
                        customerId: this.CustomerId,
                    }),
                );
            }
        }
    }
}
